import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
  modify,
} from '@pflegenavi/shared-frontend/platform';

export interface ExpenseTax {
  id: string;
  legal_entity_id: string;
  tax_name: string;
  /** Fraction between 0 and 1 as decimal string, e.g. "0.2" for 20%. */
  tax_amount: string;
  bookkeeping_code: string | null;
  archived: boolean;
}

export interface GetExpenseTaxesResponse {
  data: ExpenseTax[];
}

export function formatExpenseTaxLabel(tax: ExpenseTax): string {
  const pct = parseFloat(tax.tax_amount) * 100;
  const label = Number.isFinite(pct)
    ? `${Math.round(pct * 100) / 100}%` // trims FP noise (7.000000000000001), keeps up to 2 decimals
    : tax.tax_amount; // fall back to raw value instead of "NaN%"
  return label.trim() ? `${label} ${tax.tax_name}` : tax.tax_name;
}

export interface ExpenseSubcategorySummary {
  id: string;
  name: string;
  category: ExpenseCategory;
  archived: boolean;
}

export interface ExpensePart {
  id: string;
  amount: number;
  expense_part_sequence_nr: number;
  expense_subcategory_id: string | null;
  expense_subcategory: ExpenseSubcategorySummary | null;
  expense_tax_id: string | null;
  expense_tax: ExpenseTax | null;
}

export interface ExpenseSubcategory {
  id: string;
  name: string;
  category: ExpenseCategory;
  expense_account_id: string;
  legal_entity_id: string;
  archived: boolean;
}

export interface GetExpenseSubcategoriesResponse {
  data: ExpenseSubcategory[];
}

export interface ExpenseCashTransactionGroup {
  id: string;
  group_type: string;
  finalized_at: string | null;
  automatic_transaction_creation_allowed: boolean;
  cash_list_id: string;
  cash_list_name: string | null;
  amount: number | null;
  pliant_transaction_created_at: string | null;
  cardholder_name: string | null;
  update_date: string | null;
  pliant_transaction_types: string[] | null;
  pliant_transaction_merchant_names: string[] | null;
  pliant_transaction_categories: string[] | null;
}

export interface ExpenseExportSummary {
  id: string;
  inserted_by: string;
  inserted_at: string;
}

export const EXPENSE_CATEGORIES = [
  'ADVERTISING_AND_MARKETING',
  'COMPUTING_AND_SOFTWARE',
  'EDUCATION_AND_TRAINING',
  'ELECTRONICS_AND_IT_EQUIPMENT',
  'ENTERTAINMENT_AND_WELLNESS',
  'FOOD_AND_DRINKS',
  'GIFTS_AND_VOUCHERS',
  'MATERIALS_AND_PACKAGING',
  'OFFICE_SUPPLIES_AND_EQUIPMENT',
  'SERVICES',
  'TRAVEL_AND_ACCOMMODATION',
  'HEALTHCARE',
  'OTHER',
] as const;

export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export interface Expense {
  id: string;
  cash_list_transaction_group_id: string;
  receipt_date: string | null;
  receipt_number: string | null;
  receipt_image_ids: string[];
  receipt_not_available: boolean;
  comment: string | null;
  scheduled_for_export_at: string | null;
  expense_export_id: string | null;
  expense_export: ExpenseExportSummary | null;
  inserted_by: string;
  updated_by: string;
  inserted_at: string;
  updated_at: string;
  merchant_name: string | null;
  expense_parts: ExpensePart[];
  cash_transaction_group: ExpenseCashTransactionGroup | null;
}

export interface ExpenseListMeta {
  current_page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

export interface GetExpensesResponse {
  data: Expense[];
  meta: ExpenseListMeta;
}

export type ExpenseTab = 'open' | 'scheduled' | 'exported';

export interface ExpenseFilters {
  receiptDateFrom?: string;
  receiptDateTo?: string;
  merchantName?: string;
}

export interface GetExpensesParams {
  params: {
    /**
     * Optional — when set, scopes the list to that nursing home via a Flop
     * filter on `cash_list.nursing_home_id`. The per-NH UI always passes it;
     * a future cross-home view (e.g. expense-manager admin) may omit it.
     */
    nursingHomeId?: string;
    page: number;
    pageSize: number;
    tab: ExpenseTab;
    employeeId?: string;
    expenseExportId?: string;
    orderBy?: string;
    orderDirection?: 'asc' | 'desc';
    filters?: ExpenseFilters;
  };
}

export interface UpdateExpensePart {
  id?: string;
  amount: number;
  expense_part_sequence_nr?: number;
  expense_subcategory_id?: string | null;
  expense_tax_id?: string | null;
}

export interface CreateExpenseBody {
  cash_list_transaction_group_id: string;
  expense_parts: UpdateExpensePart[];
  comment?: string | null;
  receipt_date?: string | null;
  receipt_number?: string | null;
  receipt_image_ids?: string[];
  receipt_not_available?: boolean;
}

export interface CreateExpenseParams {
  body: CreateExpenseBody;
}

export interface CreateExpenseResponse {
  data: Expense;
}

export interface UpdateExpenseBody {
  comment?: string | null;
  receipt_date?: string | null;
  receipt_number?: string | null;
  receipt_image_ids?: string[];
  receipt_not_available?: boolean;
  expense_parts?: UpdateExpensePart[];
}

export interface UpdateExpenseParams {
  params: {
    expense_id: string;
  };
  body: UpdateExpenseBody;
}

export interface UpdateExpenseResponse {
  data: Expense;
}

export interface DeleteExpenseParams {
  params: {
    expense_id: string;
    /** Used by the hook to invalidate the per-group cache. */
    cash_list_transaction_group_id: string;
  };
}

export interface ScheduleExpensesForExportParams {
  body: {
    expense_ids: string[];
  };
}

export interface ScheduleExpensesForExportResponse {
  data: Expense[];
}

export interface CreateExpenseExportParams {
  body: {
    expense_ids: string[];
  };
}

// Creating an export returns the same CSV-ready payload as getExpenseCsvExport,
// so the client can build the file directly from the create response.
export type CreateExpenseExportResponse = GetExpenseCsvExportResponse;

export interface GetExpenseParams {
  params: { expense_id: string };
}

export interface GetExpenseResponse {
  data: Expense;
}

export interface GetExpensesByCashGroupParams {
  params: { cash_list_transaction_group_id: string };
}

export interface ExpenseCsvExportRow {
  expense_id: string;
  expense_part_sequence_number: number;
  cash_list_transaction_group_id: string;
  expense_export_id: string | null;
  legal_entity_name: string | null;
  nursing_home_name: string | null;
  cost_center: string | null;
  receipt_date: string | null;
  receipt_number: string | null;
  receipt_links: string | null;
  comment: string | null;
  amount: number;
  currency: string | null;
  tax_amount: string | null;
  tax_name: string | null;
  tax_bookkeeping_code: string | null;
  subcategory_name: string | null;
  subcategory_category: ExpenseCategory | null;
  expense_account_name: string | null;
  expense_account_number: string | null;
  group_id: string;
  cash_list_name: string | null;
  group_created_at: string | null;
  group_created_by_first_name: string | null;
  group_created_by_last_name: string | null;
  group_updated_at: string | null;
  pliant_transaction_ids: string | null;
  pliant_statuses: string | null;
  card_types: string | null;
  pliant_types: string | null;
  pliant_categories: string | null;
  pliant_merchant_names: string | null;
  pliant_mccs: string | null;
  pliant_merchant_terminal_ids: string | null;
  pliant_transaction_countries: string | null;
  pliant_created_at: string | null;
  pliant_authorized_at: string | null;
  pliant_confirmed_at: string | null;
  pliant_booked_at: string | null;
  cardholder_first_name: string | null;
  cardholder_last_name: string | null;
  card_last_four: string | null;
  expense_created_by_first_name: string | null;
  expense_created_by_last_name: string | null;
  expense_created_at: string;
  exported_by_first_name: string | null;
  exported_by_last_name: string | null;
  exported_at: string;
}

export interface GetExpenseCsvExportResponse {
  data: ExpenseCsvExportRow[];
}

export interface GetExpenseCsvExportParams {
  params: {
    expenseExportId: string;
  };
}

export interface CreateExpenseExportZipParams {
  body: {
    expense_ids: string[];
  };
}

export interface RebuildExpenseExportZipParams {
  params: {
    expenseExportId: string;
  };
  body: undefined;
}

export interface StartExpenseExportZipResponse {
  data: {
    export_id: string;
    status: ExpenseExportZipStatus['status'];
  };
}

export interface GetExpenseExportZipStatusParams {
  params: {
    expenseExportId: string;
  };
}

export type ExpenseExportZipStatus =
  | { status: 'processing' }
  | { status: 'ready'; download_url: string }
  | { status: 'failed'; error?: string };

export interface GetExpenseExportZipStatusResponse {
  data: ExpenseExportZipStatus;
}

const ZIP_POLL_INTERVAL_MS = 2000;
// The lambda streams every receipt image into the zip, so a large export takes a
// while. Give up eventually rather than polling forever on a lost callback.
const ZIP_POLL_TIMEOUT_MS = 5 * 60 * 1000;

export class ExpenseExportZipError extends Error {}

/**
 * Polls the ZIP export status until it is ready and resolves with its download
 * URL. Rejects when the build failed or took longer than `timeoutMs`.
 *
 * A 404 from `getStatus` means the backend no longer tracks the job and the
 * caller must re-trigger it - it propagates as-is.
 *
 * @example
 *     const url = await pollExpenseExportZip({
 *       getStatus: () => api.getExpenseExportZipStatus({ params: { expenseExportId } }),
 *     });
 */
export async function pollExpenseExportZip({
  getStatus,
  intervalMs = ZIP_POLL_INTERVAL_MS,
  timeoutMs = ZIP_POLL_TIMEOUT_MS,
  sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms)),
  now = () => Date.now(),
}: {
  getStatus: () => Promise<GetExpenseExportZipStatusResponse>;
  intervalMs?: number;
  timeoutMs?: number;
  sleep?: (ms: number) => Promise<unknown>;
  now?: () => number;
}): Promise<string> {
  const deadline = now() + timeoutMs;

  while (now() < deadline) {
    const { data } = await getStatus();
    if (data.status === 'ready') {
      return data.download_url;
    }
    if (data.status === 'failed') {
      throw new ExpenseExportZipError(data.error ?? 'Export failed');
    }
    await sleep(intervalMs);
  }
  throw new ExpenseExportZipError('Export timed out');
}

export const BUDGET_INTERVALS = ['monthly', 'quarterly', 'yearly'] as const;

export type BudgetInterval = (typeof BUDGET_INTERVALS)[number];

/** All amounts in cents, spent within the budget's current interval. */
export interface BudgetConsumption {
  /** Already exported. */
  locked_in: number;
  scheduled_for_export: number;
  /** Assigned to the budget but not yet scheduled ("open"). */
  just_assigned: number;
  total: number;
}

export const BUDGET_ALERT_SEVERITIES = ['info', 'warning', 'error'] as const;
export type BudgetAlertSeverity = (typeof BUDGET_ALERT_SEVERITIES)[number];

/** Which consumption stages count toward the threshold, cumulatively. */
export const BUDGET_ALERT_COUNT_LEVELS = [
  'exported',
  'scheduled',
  'all',
] as const;
export type BudgetAlertCountLevel = (typeof BUDGET_ALERT_COUNT_LEVELS)[number];

export interface BudgetAlert {
  id: string;
  /** Percent of the budget at which the alert fires. */
  threshold: number;
  severity: BudgetAlertSeverity;
  count_level: BudgetAlertCountLevel;
  send_email_notification: boolean;
  /** Set by the check job, not the API. */
  last_email_notification_sent_at: string | null;
  /** Computed server-side: whether the threshold is currently crossed. */
  active: boolean;
}

/** Alert payload for create/update. Omit `id` to create; include it to update. */
export interface BudgetAlertInput {
  id?: string;
  threshold: number;
  severity: BudgetAlertSeverity;
  count_level: BudgetAlertCountLevel;
  send_email_notification: boolean;
}

export interface Budget {
  id: string;
  nursing_home_id: string;
  name: string;
  /** Budget per interval, in cents. */
  budget_amount: number;
  interval: BudgetInterval;
  expense_subcategory_ids: string[];
  consumption: BudgetConsumption;
  alerts: BudgetAlert[];
}

const SEVERITY_RANK: Record<BudgetAlertSeverity, number> = {
  info: 1,
  warning: 2,
  error: 3,
};

/**
 * Severity of the most severe currently-active alert on the budget, or null if
 * none are active. Drives the refined threshold coloring (info/warning/error).
 */
export function activeBudgetAlertSeverity(
  budget: Budget
): BudgetAlertSeverity | null {
  return (budget.alerts ?? [])
    .filter((alert) => alert.active)
    .reduce<BudgetAlertSeverity | null>(
      (max, alert) =>
        max === null || SEVERITY_RANK[alert.severity] > SEVERITY_RANK[max]
          ? alert.severity
          : max,
      null
    );
}

export function budgetConsumedCents(budget: Budget): number {
  return budget.consumption?.total ?? 0;
}

export function budgetAvailableCents(budget: Budget): number {
  return budget.budget_amount - budgetConsumedCents(budget);
}

/** Usage in percent (0-100+, may exceed 100). Infinity when a zero budget has consumption. */
export function budgetUsagePercent(budget: Budget): number {
  if (budget.budget_amount <= 0) {
    return budgetConsumedCents(budget) > 0 ? Infinity : 0;
  }
  return (budgetConsumedCents(budget) / budget.budget_amount) * 100;
}

export function isBudgetExceeded(budget: Budget): boolean {
  return budgetConsumedCents(budget) > budget.budget_amount;
}

/**
 * Subcategory ids already claimed by a budget. A subcategory belongs to at most
 * one budget, so these must not be offered when creating or editing another
 * budget. Pass `excludeBudgetId` when editing so the budget's own subcategories
 * stay selectable.
 */
export function budgetSubcategoryIdsInUse(
  budgets: Budget[],
  excludeBudgetId?: string
): Set<string> {
  return new Set(
    budgets
      .filter((budget) => budget.id !== excludeBudgetId)
      .flatMap((budget) => budget.expense_subcategory_ids)
  );
}

export interface GetBudgetsResponse {
  data: Budget[];
  meta: {
    current_page: number;
    total_count: number;
    total_pages: number;
  };
}

export interface GetBudgetsParams {
  params: {
    nursingHomeId?: string;
    page?: number;
    pageSize?: number;
  };
}

export interface CreateBudgetBody {
  nursing_home_id: string;
  name: string;
  /** In cents. */
  budget_amount: number;
  interval: BudgetInterval;
  expense_subcategory_ids: string[];
  alerts?: BudgetAlertInput[];
}

export interface CreateBudgetParams {
  body: CreateBudgetBody;
}

export interface UpdateBudgetBody {
  name?: string;
  /** In cents. */
  budget_amount?: number;
  interval?: BudgetInterval;
  expense_subcategory_ids?: string[];
  /** Omit to leave alerts untouched; a list reconciles (create/update/delete). */
  alerts?: BudgetAlertInput[];
}

export interface UpdateBudgetParams {
  params: {
    budget_id: string;
  };
  body: UpdateBudgetBody;
}

export interface BudgetResponse {
  data: Budget;
}

export interface DeleteBudgetParams {
  params: {
    budget_id: string;
  };
}

export interface ReceiptImageAnalysis {
  receiptNumber: string | null;
  /** ISO date (YYYY-MM-DD) or null. */
  receiptDate: string | null;
  /** Total in cents, or null. */
  total: number | null;
}

export interface AnalyzeReceiptImageParams {
  imageId: string;
}

export interface IExpensePhoenixApi extends Api {
  getExpenses(dataIn: GetExpensesParams): Promise<GetExpensesResponse>;
  getExpense(dataIn: GetExpenseParams): Promise<GetExpenseResponse>;
  getExpensesByCashGroup(
    dataIn: GetExpensesByCashGroupParams
  ): Promise<GetExpensesResponse>;
  createExpense(dataIn: CreateExpenseParams): Promise<CreateExpenseResponse>;
  updateExpense(dataIn: UpdateExpenseParams): Promise<UpdateExpenseResponse>;
  deleteExpense(dataIn: DeleteExpenseParams): Promise<void>;
  scheduleExpensesForExport(
    dataIn: ScheduleExpensesForExportParams
  ): Promise<ScheduleExpensesForExportResponse>;
  unscheduleExpensesForExport(
    dataIn: ScheduleExpensesForExportParams
  ): Promise<ScheduleExpensesForExportResponse>;
  createExpenseExport(
    dataIn: CreateExpenseExportParams
  ): Promise<CreateExpenseExportResponse>;
  getExpenseCsvExport(
    dataIn: GetExpenseCsvExportParams
  ): Promise<GetExpenseCsvExportResponse>;
  createExpenseExportZip(
    dataIn: CreateExpenseExportZipParams
  ): Promise<StartExpenseExportZipResponse>;
  rebuildExpenseExportZip(
    dataIn: RebuildExpenseExportZipParams
  ): Promise<StartExpenseExportZipResponse>;
  getExpenseExportZipStatus(
    dataIn: GetExpenseExportZipStatusParams
  ): Promise<GetExpenseExportZipStatusResponse>;
  getExpenseSubcategories(): Promise<GetExpenseSubcategoriesResponse>;
  getBudgets(dataIn: GetBudgetsParams): Promise<GetBudgetsResponse>;
  createBudget(dataIn: CreateBudgetParams): Promise<BudgetResponse>;
  updateBudget(dataIn: UpdateBudgetParams): Promise<BudgetResponse>;
  deleteBudget(dataIn: DeleteBudgetParams): Promise<void>;
  analyzeReceiptImage(dataIn: {
    params: AnalyzeReceiptImageParams;
    body: undefined;
  }): Promise<ReceiptImageAnalysis>;
  getExpenseTaxes(): Promise<GetExpenseTaxesResponse>;
}

export class ExpensePhoenixApi implements IExpensePhoenixApi {
  public getExpenses: IExpensePhoenixApi['getExpenses'];
  public getExpense: IExpensePhoenixApi['getExpense'];
  public getExpensesByCashGroup: IExpensePhoenixApi['getExpensesByCashGroup'];
  public createExpense: IExpensePhoenixApi['createExpense'];
  public updateExpense: IExpensePhoenixApi['updateExpense'];
  public deleteExpense: IExpensePhoenixApi['deleteExpense'];
  public scheduleExpensesForExport: IExpensePhoenixApi['scheduleExpensesForExport'];
  public unscheduleExpensesForExport: IExpensePhoenixApi['unscheduleExpensesForExport'];
  public createExpenseExport: IExpensePhoenixApi['createExpenseExport'];
  public getExpenseCsvExport: IExpensePhoenixApi['getExpenseCsvExport'];
  public createExpenseExportZip: IExpensePhoenixApi['createExpenseExportZip'];
  public rebuildExpenseExportZip: IExpensePhoenixApi['rebuildExpenseExportZip'];
  public getExpenseExportZipStatus: IExpensePhoenixApi['getExpenseExportZipStatus'];
  public getExpenseSubcategories: IExpensePhoenixApi['getExpenseSubcategories'];
  public getBudgets: IExpensePhoenixApi['getBudgets'];
  public createBudget: IExpensePhoenixApi['createBudget'];
  public updateBudget: IExpensePhoenixApi['updateBudget'];
  public deleteBudget: IExpensePhoenixApi['deleteBudget'];
  public analyzeReceiptImage: IExpensePhoenixApi['analyzeReceiptImage'];
  public getExpenseTaxes: IExpensePhoenixApi['getExpenseTaxes'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    const baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);

    this.createExpense = modify({
      authContext,
      method: 'POST',
      url: () => `${baseUrl}/expense`,
    });

    this.analyzeReceiptImage = modify<
      undefined,
      ReceiptImageAnalysis,
      ReceiptImageAnalysis,
      AnalyzeReceiptImageParams
    >({
      authContext,
      method: 'POST',
      url: (params) => `${baseUrl}/receipt-image/${params.imageId}/analyze`,
    });

    this.updateExpense = modify({
      authContext,
      method: 'PATCH',
      url: (params) => `${baseUrl}/expense/${params.expense_id}`,
    });

    this.deleteExpense = modify({
      authContext,
      method: 'DELETE',
      url: (params) => `${baseUrl}/expense/${params.expense_id}`,
    });

    this.scheduleExpensesForExport = modify({
      authContext,
      method: 'POST',
      url: () => `${baseUrl}/expense/schedule-for-export`,
    });

    this.unscheduleExpensesForExport = modify({
      authContext,
      method: 'POST',
      url: () => `${baseUrl}/expense/unschedule-for-export`,
    });

    this.createExpenseExport = modify({
      authContext,
      method: 'POST',
      url: () => `${baseUrl}/expense-export`,
    });

    this.getExpenses = get({
      authContext,
      url: (params) => {
        const query = new URLSearchParams();

        query.append('page', params.page.toString());
        query.append('page_size', params.pageSize.toString());

        let i = 0;

        if (params.nursingHomeId) {
          query.append(`filters[${i}][field]`, 'nursing_home_id');
          query.append(`filters[${i}][op]`, '==');
          query.append(`filters[${i}][value]`, params.nursingHomeId);
          i++;
        }

        // Tab-based filters using Flop operators
        if (params.tab === 'open') {
          query.append(`filters[${i}][field]`, 'scheduled_for_export_at');
          query.append(`filters[${i}][op]`, 'empty');
          query.append(`filters[${i}][value]`, 'true');
          i++;
          query.append(`filters[${i}][field]`, 'expense_export_id');
          query.append(`filters[${i}][op]`, 'empty');
          query.append(`filters[${i}][value]`, 'true');
          i++;
        } else if (params.tab === 'scheduled') {
          query.append(`filters[${i}][field]`, 'scheduled_for_export_at');
          query.append(`filters[${i}][op]`, 'not_empty');
          query.append(`filters[${i}][value]`, 'true');
          i++;
          query.append(`filters[${i}][field]`, 'expense_export_id');
          query.append(`filters[${i}][op]`, 'empty');
          query.append(`filters[${i}][value]`, 'true');
          i++;
        } else if (params.tab === 'exported') {
          query.append(`filters[${i}][field]`, 'expense_export_id');
          query.append(`filters[${i}][op]`, 'not_empty');
          query.append(`filters[${i}][value]`, 'true');
          i++;
        }

        if (params.employeeId) {
          query.append(`filters[${i}][field]`, 'inserted_by');
          query.append(`filters[${i}][op]`, '==');
          query.append(`filters[${i}][value]`, params.employeeId);
          i++;
        }

        if (params.expenseExportId) {
          query.append(`filters[${i}][field]`, 'expense_export_id');
          query.append(`filters[${i}][op]`, '==');
          query.append(`filters[${i}][value]`, params.expenseExportId);
          i++;
        }

        const filters = params.filters;
        if (filters?.receiptDateFrom) {
          query.append(`filters[${i}][field]`, 'receipt_date');
          query.append(`filters[${i}][op]`, '>=');
          query.append(`filters[${i}][value]`, filters.receiptDateFrom);
          i++;
        }
        if (filters?.receiptDateTo) {
          query.append(`filters[${i}][field]`, 'receipt_date');
          query.append(`filters[${i}][op]`, '<=');
          query.append(`filters[${i}][value]`, filters.receiptDateTo);
          i++;
        }
        if (filters?.merchantName) {
          query.append(`filters[${i}][field]`, 'merchant_name');
          query.append(`filters[${i}][op]`, 'ilike_and');
          query.append(`filters[${i}][value]`, filters.merchantName);
          i++;
        }

        if (params.orderBy) {
          query.append('order_by[]', params.orderBy);
          query.append('order_directions[]', params.orderDirection ?? 'desc');
        }

        return `${baseUrl}/expense?${query.toString()}`;
      },
    });

    this.getExpenseCsvExport = get({
      authContext,
      url: (params) =>
        `${baseUrl}/expense/csv-export/${params.expenseExportId}`,
    });

    this.createExpenseExportZip = modify({
      authContext,
      method: 'POST',
      url: () => `${baseUrl}/expense-export/zip`,
    });

    this.rebuildExpenseExportZip = modify({
      authContext,
      method: 'POST',
      url: (params) =>
        `${baseUrl}/expense-export/zip/${params.expenseExportId}/rebuild`,
    });

    this.getExpenseExportZipStatus = get({
      authContext,
      url: (params) =>
        `${baseUrl}/expense-export/zip/${params.expenseExportId}`,
    });

    this.getExpenseSubcategories = get({
      authContext,
      url: `${baseUrl}/expense-subcategory`,
    });

    this.getBudgets = get({
      authContext,
      url: (params) => {
        const query = new URLSearchParams();
        query.append('page', (params.page ?? 1).toString());
        query.append('page_size', (params.pageSize ?? 100).toString());
        if (params.nursingHomeId) {
          query.append('filters[0][field]', 'nursing_home_id');
          query.append('filters[0][op]', '==');
          query.append('filters[0][value]', params.nursingHomeId);
        }
        query.append('order_by[]', 'name');
        query.append('order_directions[]', 'asc');
        return `${baseUrl}/expense-budget?${query.toString()}`;
      },
    });

    this.createBudget = modify({
      authContext,
      method: 'POST',
      url: () => `${baseUrl}/expense-budget`,
    });

    this.updateBudget = modify({
      authContext,
      method: 'PATCH',
      url: (params) => `${baseUrl}/expense-budget/${params.budget_id}`,
    });

    this.deleteBudget = modify({
      authContext,
      method: 'DELETE',
      url: (params) => `${baseUrl}/expense-budget/${params.budget_id}`,
    });

    this.getExpenseTaxes = get({
      authContext,
      url: `${baseUrl}/expense-tax`,
    });

    this.getExpense = get({
      authContext,
      url: (params) => `${baseUrl}/expense/${params.expense_id}`,
    });

    this.getExpensesByCashGroup = get({
      authContext,
      url: (params) => {
        const query = new URLSearchParams();
        query.append('filters[0][field]', 'cash_list_transaction_group_id');
        query.append('filters[0][op]', '==');
        query.append(
          'filters[0][value]',
          params.cash_list_transaction_group_id
        );
        return `${baseUrl}/expense?${query.toString()}`;
      },
    });
  }
}
